import type { MatchConfig } from "@meleeroyale/shared";
import { emptyInput, type PlayerInput } from "./input.ts";
import { cos, sin } from "./math/trig.ts";
import { copy, type Vec3, v3 } from "./math/vec.ts";
import { createRng, type Rng } from "./rng.ts";
import { idleFoot, idleHand, PART_COUNT } from "./skeleton.ts";
import { TICK_RATE, tuning } from "./tuning.ts";

export type MatchPhase = "countdown" | "fight" | "ended";

export interface StepState {
  ticksLeft: number;
  /** World-space direction, unit length on the ground plane. */
  dir: Vec3;
}

/** Limbs as strike channels: 0 left arm, 1 right arm, 2 left leg, 3 right leg. */
export type Limb = 0 | 1 | 2 | 3;
/** Arc shapes: 0 straight, 1 around, 2 rising. */
export type Arc = 0 | 1 | 2;
/** Target zones, picked by key (design §5.1): 0 head, 1 body, 2 legs. */
export type Zone = 0 | 1 | 2;
export type StrikePhase = "windup" | "throw" | "recovery";

/** A limb being loaded while its button is held. */
export interface ChargeState {
  limb: Limb;
  arc: Arc;
  /** The zone it goes for, read at the press. */
  zone: Zone;
  ticks: number;
}

/** A press waiting its turn in a combo: limb, arc and zone as they were at the press. */
export interface QueuedStrike {
  limb: Limb;
  arc: Arc;
  zone: Zone;
}

/**
 * How a fighter is built (design §4.5), 1 = balanced. `speed` (agility) scales
 * how fast limbs move: tip-speed limits, wind-up and recovery, the guard's
 * arms. `strength` scales the mass behind a strike, the body mass a shove has
 * to move and how much of a hit the body shrugs off.
 */
export interface Build {
  speed: number;
  strength: number;
}

export const BALANCED: Build = { speed: 1, strength: 1 };

/** A strike in flight. Path points are in fighter-local space. */
export interface StrikeState {
  limb: Limb;
  arc: Arc;
  phase: StrikePhase;
  /** Ticks elapsed in the current phase. */
  ticks: number;
  /** 0 tap .. 1 fully charged. */
  charge: number;
  /** Wind-up ticks still owed before the throw starts (a tap released early). */
  windupTicks: number;
  throwTicks: number;
  recoveryTicks: number;
  origin: Vec3;
  control: Vec3;
  aim: Vec3;
  /** The aim point anchored in world space; the local path is re-solved toward it every tick. */
  aimWorld: Vec3;
  /** Local position the limb returns to after the throw. */
  rest: Vec3;
  /** Tip position at the end of the throw (local), start of recovery. */
  end: Vec3;
  /** Previous tick's tip in world space, for the sweep. */
  tipPrev: Vec3;
  /** 0..1 along the path (past 1 during follow-through). */
  progress: number;
  /**
   * Length of the path planned when the throw began (metres). The limb covers
   * that distance at its own speed, so a target that comes closer (a step in)
   * is reached sooner and the step's speed adds to the arm's.
   */
  pathLength: number;
  /**
   * How much this limb led when it was thrown: +1 the lead side, −1 the rear
   * side, in between during a stance switch. Sets the kinetic chain.
   */
  lead: number;
  hit: boolean;
  /** Set when the strike met a guard (arm or checking shin) rather than a body. */
  blocked: boolean;
  /** Set once the throw finished without touching anything. */
  whiffed: boolean;
}

export interface ParryState {
  arm: 0 | 1;
  ticksLeft: number;
  /** Ticks the push lasts in total. */
  total: number;
}

/** Where a fighter stood at the end of a tick, kept briefly for lag compensation. */
export interface PastPose {
  /** The tick this pose ended; -1 for an empty slot. */
  tick: number;
  x: number;
  z: number;
  facing: number;
}

/** Ring size: every tick a strike may be rewound to, plus one. */
export const PAST_POSES = tuning.lagCompensation.maxRewindTicks + 1;

export interface FighterState {
  id: number;
  nickname: string;
  isBot: boolean;
  build: Build;
  alive: boolean;
  /** Feet position; y is 0 on the floor. */
  pos: Vec3;
  /** Body velocity this tick (movement + step + impulse), world space. */
  vel: Vec3;
  /** Residual impulse velocity from shoves and hits; decays every tick. */
  impulse: Vec3;
  /** Body yaw in radians; follows look yaw at a finite turn rate. */
  facing: number;
  lookYaw: number;
  lookPitch: number;
  hp: number[];
  stamina: number;
  exhausted: boolean;
  guard: boolean;
  /** 0 standing .. 1 fully ducked. */
  duck: number;
  /** Slip: -1 fully leaned to the fighter's left .. 1 to its right. */
  lean: number;
  /** Stance, +1 orthodox (left foot leads) .. −1 southpaw; moves toward the target during a switch. */
  stance: number;
  /** The stance being held or switched to: true for southpaw. */
  southpaw: boolean;
  /**
   * Torso turn added by strikes in flight, in blade units (the shoulders' and
   * hips' rotation is `stance + twist`): a rear strike turns the body through.
   */
  twist: number;
  /** Covering low: the lead knee is up to check a kick. */
  checking: boolean;
  /** The zone the guard covers (Space, with F for the body or Shift for the legs), or -1. */
  coverZone: Zone | -1;
  step: StepState | null;
  /** Hands and feet in fighter-local space (posed by guard, idle or a strike). */
  handL: Vec3;
  handR: Vec3;
  footL: Vec3;
  footR: Vec3;
  chargeArm: ChargeState | null;
  chargeLeg: ChargeState | null;
  strikeArm: StrikeState | null;
  strikeLeg: StrikeState | null;
  /** Presses waiting their turn in a combo, oldest first. */
  queue: QueuedStrike[];
  /** Strikes started in the current combo (0 when the fighter is idle). */
  chain: number;
  parry: ParryState | null;
  /** Ticks left unable to act after a deflection or a heavy hit. */
  stagger: number;
  /** Ticks left before this fighter may throw again (initiative rule). */
  initiativeLock: number;
  /** Ticks this fighter has been overlapping another while pinned to the wall. */
  crushTicks: number;
  /** Total damage dealt, for the time-out tie-break. */
  damageDealt: number;
  lastHitBy: number;
  lastInput: PlayerInput;
  prevButtons: number;
  /** Ring of recent end-of-tick positions and facings, indexed by tick % PAST_POSES. */
  past: PastPose[];
}

export interface CageState {
  /** Current apothem (centre to wall). */
  r: number;
  targetR: number;
  /** Shrink phase index (0 before the first shrink). */
  phase: number;
  /** Tick (fight-relative) when the next shrink starts. */
  nextShrinkTick: number;
  /** True during the warning window before a shrink. */
  warning: boolean;
}

export interface MatchState {
  config: MatchConfig;
  phase: MatchPhase;
  tick: number;
  /** Tick at which the fight phase began (countdown end). */
  fightStartTick: number;
  /** Fight ticks remaining on the 3-minute clock. */
  clockTicks: number;
  cage: CageState;
  fighters: FighterState[];
  rng: Rng;
  winner: number;
  endReason: "lastStanding" | "timeout" | "none";
}

export interface SpawnSlot {
  nickname: string;
  isBot: boolean;
  /** Balanced when left out. */
  build?: Build;
}

/** Cage apothem for a fighter count: the tuning table's points, linear in between, clamped. */
export function cageApothem(fighters: number): number {
  const points = tuning.cage.r0;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return 10;
  if (fighters <= first[0]) return first[1];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (a && b && fighters <= b[0])
      return a[1] + ((b[1] - a[1]) * (fighters - a[0])) / (b[0] - a[0]);
  }
  return last[1];
}

/** Starting HP per body part, indexed by `Part`. */
export const PART_MAX_HP: readonly number[] = [
  tuning.hp.head,
  tuning.hp.torso,
  tuning.hp.upperArm,
  tuning.hp.upperArm,
  tuning.hp.forearm,
  tuning.hp.forearm,
  tuning.hp.thigh,
  tuning.hp.thigh,
  tuning.hp.shin,
  tuning.hp.shin,
];

export function createFighter(
  id: number,
  slot: SpawnSlot,
  pos: Vec3,
  facing: number,
): FighterState {
  const hp = PART_MAX_HP.slice(0, PART_COUNT);
  return {
    id,
    nickname: slot.nickname,
    isBot: slot.isBot,
    build: { ...(slot.build ?? BALANCED) },
    alive: true,
    pos: copy(pos),
    vel: v3(),
    impulse: v3(),
    facing,
    lookYaw: 0,
    lookPitch: 0,
    hp,
    stamina: tuning.stamina.max,
    exhausted: false,
    guard: false,
    duck: 0,
    lean: 0,
    stance: 1,
    southpaw: false,
    twist: 0,
    checking: false,
    coverZone: -1,
    step: null,
    handL: idleHand(-1, 0, 0, 1),
    handR: idleHand(1, 0, 0, 1),
    footL: idleFoot(-1, 1),
    footR: idleFoot(1, 1),
    chargeArm: null,
    chargeLeg: null,
    strikeArm: null,
    strikeLeg: null,
    queue: [],
    chain: 0,
    parry: null,
    stagger: 0,
    initiativeLock: 0,
    crushTicks: 0,
    damageDealt: 0,
    lastHitBy: -1,
    lastInput: emptyInput(0),
    prevButtons: 0,
    past: Array.from({ length: PAST_POSES }, () => ({ tick: -1, x: 0, z: 0, facing: 0 })),
  };
}

/** Fighters spawn evenly on a ring at 60 % of the cage apothem, facing the centre. */
export function createMatch(config: MatchConfig, slots: readonly SpawnSlot[]): MatchState {
  const r0 = cageApothem(config.sessionSize);
  const ring = r0 * 0.6;
  const fighters: FighterState[] = [];
  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i];
    if (!slot) continue;
    const angle = (i / slots.length) * Math.PI * 2;
    const pos = v3(sin(angle) * ring, 0, cos(angle) * ring);
    // Face the centre: the direction from pos to origin has yaw angle + π.
    fighters.push(createFighter(i, slot, pos, angle + Math.PI));
  }
  return {
    config,
    phase: "countdown",
    tick: 0,
    fightStartTick: Math.round(tuning.match.countdownSeconds * TICK_RATE),
    clockTicks: tuning.match.seconds * TICK_RATE,
    cage: {
      r: r0,
      targetR: r0,
      phase: 0,
      nextShrinkTick: tuning.cage.shrinkEverySeconds * TICK_RATE,
      warning: false,
    },
    fighters,
    rng: createRng(config.seed),
    winner: -1,
    endReason: "none",
  };
}

export function aliveCount(state: MatchState): number {
  let n = 0;
  for (const f of state.fighters) if (f.alive) n += 1;
  return n;
}

export function totalHp(f: FighterState): number {
  let sum = 0;
  for (const hp of f.hp) sum += hp;
  return sum;
}

export const MAX_TOTAL_HP =
  tuning.hp.head +
  tuning.hp.torso +
  2 * (tuning.hp.upperArm + tuning.hp.forearm + tuning.hp.thigh + tuning.hp.shin);
